import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;

    // At the cap the plus button stays clickable (aria-disabled, not native disabled)
    // so a click can surface the quantity limit notice instead of silently doing nothing.
    const atLimit = effectiveMax !== null && value >= effectiveMax;
    plusButton.disabled = false;
    plusButton.classList.toggle('is-at-limit', atLimit);
    if (atLimit) {
      plusButton.setAttribute('aria-disabled', 'true');
    } else {
      plusButton.removeAttribute('aria-disabled');
    }
  }

  /**
   * Handles the quantity increase event.
   * At the cap, announces the limit instead of incrementing.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (effectiveMax !== null && value >= effectiveMax) {
      event.preventDefault();
      this.#dispatchLimitEvent(effectiveMax);
      return;
    }

    super.increaseQuantity(event);
  }

  /**
   * Handles a typed quantity. Announces the limit when the typed value exceeds the max
   * (the base class clamps the value back to the max).
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const typedValue = parseInt(event.target.value, 10);
    const effectiveMax = this.getEffectiveMax();

    super.setQuantity(event);

    if (effectiveMax !== null && !Number.isNaN(typedValue) && typedValue > effectiveMax) {
      this.#dispatchLimitEvent(effectiveMax);
    }
  }

  /**
   * Dispatches a bubbling event so the cart items component can reveal the limit notice.
   * @param {number} max - The quantity cap for this line.
   */
  #dispatchLimitEvent(max) {
    const line = Number(this.refs.quantityInput.dataset.cartLine);
    this.dispatchEvent(
      new CustomEvent('cart-quantity-limit', {
        bubbles: true,
        detail: { line, max },
      })
    );
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
